import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import config from '../config.js';
import { BOOST_STATUS_POLL_MS, BOOST_STATUS_POLL_TRIES, newIdempotencyKey } from './useBoostPayment.js';

/** Codes that send a skin purchase to Stripe Checkout instead, with a new key (ENG-738). */
const CHECKOUT_FALLBACK = new Set(['saved_card_required', 'card_authentication_required']);

/** Stripe couldn't be reached; the same key may be sent again safely. */
const RETRY_SAME_KEY = 'queue_payment_unavailable';

/**
 * Answers that mean the price the listener saw is not the price now (ENG-825):
 * nothing was charged, and the offer must be read again before another tap.
 * `skin_is_free` comes only from Checkout when the skin (and the expected
 * price) is 0; it is read again the same way so the free button shows.
 */
const PRICE_MOVED = new Set(['skin_price_changed', 'skin_not_for_sale', 'skin_is_free']);

/** Every edition has an owner (409, or a settled purchase's failure_code): the item is read again (ENG-833). */
const SOLD_OUT = 'skin_sold_out';

/** A price as the backend sent it: a whole number of cents, 0 or more; null otherwise. */
const offerPrice = (value) => (Number.isSafeInteger(value) && value >= 0 ? value : null);

/** The backend's current price from a price-guard 409: an integer, or null when no longer for sale. */
const currentPrice = (err) => {
  if (err?.code === 'skin_not_for_sale') return null;
  return offerPrice(err?.params?.current_price_cents);
};

const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/** The page the listener is on, as a same-app path with no query or hash (Checkout's return_path). */
const currentPath = () => {
  if (typeof window === 'undefined') return '/';
  const path = window.location.pathname || '/';
  return path.startsWith('/') ? path : `/${path}`;
};

/**
 * Buys a skin from the try-on panel's "Get it" (Skins, mockup 36, ENG-737
 * against ENG-738), the way a boost or paid request is paid for:
 *
 * - With a saved card (`GET /me/payment-method`), one tap charges it
 *   (`POST /me/skins/purchases`). 201 confirmed: the skin is owned and
 *   equipped. 202 (pending or refunded): the purchase is read at
 *   `GET /me/skins/purchases/{purchaseId}` (the tip_id) until it settles.
 * - Without one, or when the bank wants the listener to confirm, Stripe
 *   Checkout takes the card (`POST /me/skins/purchases/checkout`, a new key)
 *   and comes back to the page the listener was on (its path, no query) with
 *   `?skin=success&tip=<id>` (then the purchase is read until it settles) or
 *   `?skin=cancelled`.
 *
 * - A free skin (priceCents 0, ENG-825/827) is one request to
 *   `POST /me/skins/purchases`: no saved card is read and Checkout is never
 *   used.
 *
 * Every purchase sends `expected_price_cents`: the offer's `priceCents`
 * exactly as the backend sent it (ENG-825 price guard). `buy(skinId,
 * { priceCents, name, onSale })` takes it from the offer on screen (`onSale`:
 * that price was a sale price, ENG-832); without a valid price nothing is
 * sent. When the price moved (409 `skin_price_changed`, or
 * `skin_not_for_sale`), nothing was charged: `priceChange` is set to
 * `{ skinId, name, fromCents, toCents, wasOnSale }` (`toCents` null = no
 * longer for sale),`onPriceChanged({ skinId, priceCents })` asks the page to read the
 * offer again, and nothing is retried until the listener taps again.
 * When the last edition went to someone else (409 `skin_sold_out`, or a
 * purchase that settles with that failure_code), `onSoldOut({ skinId })`
 * asks the page to read the item again, so it flips to Sold out (ENG-833).
 *
 * Skins are SongShake's, not a band's (ENG-820/821): no band id is sent.
 *
 * One key per attempt, and nothing while one is under way, so a double click
 * buys once; after `queue_payment_unavailable` the next press of the same
 * skin re-sends the same key. `onPurchased({ tipId, skinId, editionNumber,
 * amountCents })` follows a confirmed purchase. `returnPath` is the path
 * Checkout comes back to (the shop passes `/shop`, ENG-823); without it, the
 * page the listener is on. `state` is idle, buying,
 * redirecting or failed (with `error`, a stable code).
 */
export const useSkinPurchase = ({ onPurchased, onPriceChanged, onSoldOut, returnPath = null } = {}) => {
  const fetchWrapperRef = useRef(useFetchWrapper());
  const onPurchasedRef = useRef(onPurchased);
  useEffect(() => { onPurchasedRef.current = onPurchased; }, [onPurchased]);
  const onPriceChangedRef = useRef(onPriceChanged);
  useEffect(() => { onPriceChangedRef.current = onPriceChanged; }, [onPriceChanged]);
  const onSoldOutRef = useRef(onSoldOut);
  useEffect(() => { onSoldOutRef.current = onSoldOut; }, [onSoldOut]);
  const returnPathRef = useRef(returnPath);
  returnPathRef.current = returnPath;
  const [state, setState] = useState('idle');
  const [error, setError] = useState(null);
  const [priceChange, setPriceChange] = useState(null);
  const busyRef = useRef(false);
  const pendingRef = useRef(null);
  // The key of an attempt Stripe never answered: { skinId, priceCents, key }.
  const retryRef = useRef(null);

  const fail = useCallback((code) => {
    busyRef.current = false;
    pendingRef.current = null;
    setState('failed');
    setError(code);
  }, []);

  const settle = useCallback((outcome) => {
    if (outcome?.status === 'confirmed') {
      busyRef.current = false;
      pendingRef.current = null;
      setState('idle');
      setError(null);
      onPurchasedRef.current?.({
        tipId: outcome.tip_id ?? null,
        skinId: outcome.skin_id ?? null,
        editionNumber: outcome.edition_number ?? null,
        amountCents: outcome.amount_cents ?? null,
      });
      return true;
    }
    if (outcome?.status === 'refunded') {
      fail('skin_refunded');
      return true;
    }
    if (outcome?.status === 'failed') {
      fail(outcome.failure_code || 'card_declined');
      if (outcome.failure_code === SOLD_OUT) onSoldOutRef.current?.({ skinId: outcome.skin_id ?? null });
      return true;
    }
    return false;
  }, [fail]);

  // Reads a purchase until it settles or the tries run out.
  const follow = useCallback(async (tipId) => {
    pendingRef.current = tipId;
    for (let i = 0; i < BOOST_STATUS_POLL_TRIES; i += 1) {
      if (pendingRef.current !== tipId) return;
      try {
        const outcome = await fetchWrapperRef.current.get(`${config.apiBaseUrl}/me/skins/purchases/${tipId}`);
        if (outcome?.status && outcome.status !== 'pending' && settle(outcome)) return;
      } catch (_err) {
        // Try again.
      }
      await wait(BOOST_STATUS_POLL_MS);
    }
    if (pendingRef.current === tipId) fail('skin_pending');
  }, [settle, fail]);

  const checkout = useCallback(async (skinId, priceCents) => {
    const answer = await fetchWrapperRef.current.post(`${config.apiBaseUrl}/me/skins/purchases/checkout`, {
      skin_id: skinId,
      idempotency_key: newIdempotencyKey(),
      expected_price_cents: priceCents,
      return_path: returnPathRef.current || currentPath(),
    });
    setState('redirecting');
    window.location.assign(answer.checkout_url);
  }, []);

  // The price moved under the listener: say so, have the offer read again, retry nothing.
  const priceMoved = useCallback((err, skinId, fromCents, name, onSale) => {
    const isFree = err.code === 'skin_is_free';
    const toCents = isFree ? 0 : currentPrice(err);
    setPriceChange(isFree ? null : { skinId, name: name || null, fromCents, toCents, wasOnSale: onSale === true });
    fail(err.code);
    onPriceChangedRef.current?.({ skinId, priceCents: toCents });
  }, [fail]);

  const buy = useCallback(async (skinId, { priceCents, name = null, onSale = false } = {}) => {
    if (!skinId || busyRef.current) return;
    const expected = offerPrice(priceCents);
    setPriceChange(null);
    // No price from the backend, no purchase: the request would only be refused.
    if (expected === null) {
      setState('failed');
      setError('skin_purchase_failed');
      return;
    }
    busyRef.current = true;
    setState('buying');
    setError(null);
    const free = expected === 0;
    try {
      if (!free) {
        let card = null;
        try {
          card = await fetchWrapperRef.current.get(`${config.apiBaseUrl}/me/payment-method`);
        } catch (_err) {
          card = null;
        }
        if (!card?.hasSavedCard) {
          retryRef.current = null;
          await checkout(skinId, expected);
          return;
        }
      }
      const retry = retryRef.current;
      const key = retry?.skinId === skinId && retry.priceCents === expected ? retry.key : newIdempotencyKey();
      retryRef.current = null;
      try {
        const outcome = await fetchWrapperRef.current.post(`${config.apiBaseUrl}/me/skins/purchases`, {
          skin_id: skinId,
          idempotency_key: key,
          expected_price_cents: expected,
        });
        if (outcome?.status === 'pending' || (outcome?.status === 'refunded' && outcome.tip_id)) {
          await follow(outcome.tip_id);
          return;
        }
        if (!settle(outcome)) fail('skin_purchase_failed');
      } catch (err) {
        // A free skin is never sent to Checkout.
        if (!free && CHECKOUT_FALLBACK.has(err?.code)) {
          await checkout(skinId, expected);
          return;
        }
        if (err?.code === RETRY_SAME_KEY) retryRef.current = { skinId, priceCents: expected, key };
        // Already owning a free skin: it's theirs, and nothing was charged.
        if (free && err?.code === 'skin_already_owned') {
          settle({ status: 'confirmed', skin_id: skinId });
          return;
        }
        throw err;
      }
    } catch (err) {
      if (PRICE_MOVED.has(err?.code)) {
        priceMoved(err, skinId, expected, name, onSale);
        return;
      }
      fail(err?.code || 'skin_purchase_failed');
      // The last edition went to someone else: have the page read the item again so it says Sold out (ENG-833).
      if (err?.code === SOLD_OUT) onSoldOutRef.current?.({ skinId });
    }
  }, [checkout, follow, settle, fail, priceMoved]);

  // Back from Checkout: ?skin=success&tip=<id> or ?skin=cancelled.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const result = url.searchParams.get('skin');
    if (!result) return;
    const tipId = Number(url.searchParams.get('tip'));
    url.searchParams.delete('skin');
    url.searchParams.delete('tip');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    if (result === 'success' && tipId > 0) {
      busyRef.current = true;
      setState('buying');
      follow(tipId);
    }
  }, [follow]);

  const reset = useCallback(() => {
    busyRef.current = false;
    pendingRef.current = null;
    setState('idle');
    setError(null);
    setPriceChange(null);
  }, []);

  return { buy, state, error, priceChange, reset };
};

export default useSkinPurchase;
